/** Skeleton shimmer primitives for loading states */

import { cn } from './utils';

interface SkeletonProps {
  className?: string;
}

function Shimmer({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-xl bg-black/10 dark:bg-white/10',
        className
      )}
    />
  );
}

/** Portrait-ratio card skeleton (used in DiscoverScreen) */
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('relative flex flex-col gap-2 overflow-hidden rounded-2xl', className)}>
      <Shimmer className="aspect-[2/3] w-full rounded-2xl" />
      <Shimmer className="h-4 w-3/4 mx-auto" />
      <Shimmer className="h-3 w-1/2 mx-auto" />
    </div>
  );
}

/** Horizontal yap-card row skeleton (YapTab, profile, etc.) */
export function SkeletonYap({ className }: SkeletonProps) {
  return (
    <div className={cn('flex gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10', className)}>
      {/* Avatar */}
      <Shimmer className="w-10 h-10 rounded-full shrink-0" />
      <div className="flex-1 flex flex-col gap-2">
        {/* Username row */}
        <div className="flex gap-2">
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-3 w-16" />
        </div>
        {/* Body lines */}
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-4/5" />
        <Shimmer className="h-3 w-1/2" />
        {/* Action row */}
        <div className="flex gap-4 mt-1">
          <Shimmer className="h-3 w-10" />
          <Shimmer className="h-3 w-10" />
          <Shimmer className="h-3 w-10" />
        </div>
      </div>
    </div>
  );
}

/** Tribe / forum tile skeleton (ExploreScreen) */
export function SkeletonTile({ className }: SkeletonProps) {
  return (
    <div className={cn('flex gap-3 p-3 rounded-2xl border border-black/10 dark:border-white/10 items-center', className)}>
      <Shimmer className="w-14 h-14 rounded-xl shrink-0" />
      <div className="flex-1 flex flex-col gap-2">
        <Shimmer className="h-3 w-32" />
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-20" />
      </div>
    </div>
  );
}

/** Notification row skeleton */
export function SkeletonNotification({ className }: SkeletonProps) {
  return (
    <div className={cn('flex gap-3 p-4 items-start', className)}>
      <Shimmer className="w-12 h-12 rounded-full shrink-0" />
      <div className="flex-1 flex flex-col gap-2">
        <Shimmer className="h-3 w-40" />
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-2 w-16" />
      </div>
    </div>
  );
}

/** Profile header skeleton */
export function SkeletonProfileHeader({ className }: SkeletonProps) {
  return (
    <div className={cn('flex flex-col gap-0', className)}>
      {/* Banner */}
      <Shimmer className="w-full h-40" />
      {/* Avatar row */}
      <div className="px-4 -mt-10 flex justify-between items-end mb-3">
        <Shimmer className="w-20 h-20 rounded-full border-4 border-white dark:border-[#0D0D0D]" />
        <Shimmer className="h-9 w-28 rounded-full" />
      </div>
      {/* Name */}
      <div className="px-4 flex flex-col gap-2">
        <Shimmer className="h-5 w-40" />
        <Shimmer className="h-3 w-24" />
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-3/4" />
      </div>
    </div>
  );
}
